"use client"

import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import VideoElement from "@/app/(site)/_components/SanityElements/VideoElement"
import { useImageGalleryScroll } from "@/app/(site)/_utils/_hooks/useImageGalleryScroll"
import { type MediaItem } from "@/lib/content-service/types/ContentServiceProduct"
import {
    FOCUS_VISIBLE_CLASS,
    FOCUS_VISIBLE_INSET_CLASS
} from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"
import { type ImageType, type ProductGalleryVideo } from "@/types"
import placeholder from "@assets/images/placeholder.png"
import dynamic from "next/dynamic"
import Image from "next/image"
import { type KeyboardEvent, useMemo, useState } from "react"

const FullscreenImagePopup = dynamic(
    () => import("@/app/(site)/_components/Page/Product/ProductDetail/FullscreenImage"),
    { ssr: false }
)

type ProductImageGalleryProps = {
    media?: MediaItem[]
    productName: string
    productGalleryVideo: ProductGalleryVideo | undefined
    langCode: string
}

export type CombinedMediaItem =
    | {
          _type: "video"
          vimeoId: string
          videoFileUrl?: string
          poster: ImageType | null
          loop?: boolean
      }
    | { _type: "image"; image: string }

export default function ProductImageGallery({
    media,
    productName,
    productGalleryVideo,
    langCode
}: ProductImageGalleryProps) {
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
    const {
        currentIndex: currentImageIndex,
        setCurrentIndex,
        containerRef,
        scrollToIndex
    } = useImageGalleryScroll()
    const imageSizesProduct =
        "(min-width: 1920px) 41vw, (min-width: 1280px) 43vw, (min-width: 1024px) 37vw, (min-width: 640px) 100vw, 48vw"

    const galleryMediaItems = useMemo(() => {
        const combinedMedia: CombinedMediaItem[] = []

        media?.forEach(image => {
            combinedMedia.push({
                _type: "image",
                image: image.url || ""
            })
        })

        if (productGalleryVideo) {
            const targetIndex = Math.min(
                Math.max(productGalleryVideo.index - 1, 0),
                combinedMedia.length
            )

            combinedMedia.splice(targetIndex, 0, {
                _type: "video",
                vimeoId: productGalleryVideo.vimeoId,
                videoFileUrl: productGalleryVideo.videoFileUrl,
                poster: productGalleryVideo.poster,
                loop: productGalleryVideo.loop
            })
        }

        return combinedMedia
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media, JSON.stringify(productGalleryVideo)])

    const mediaCount = galleryMediaItems.length
    const lastIndex = mediaCount - 1

    const handleImageClick = (index: number) => {
        if (mediaCount === 0) return
        setSelectedImageIndex(index)
    }

    const handleIndicatorClick = (index: number) => {
        setCurrentIndex(index)
        scrollToIndex(index)
    }

    const handleGalleryKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (mediaCount === 0) return

        switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
                event.preventDefault()
                scrollToIndex(Math.min(currentImageIndex + 1, lastIndex))
                return
            case "ArrowLeft":
            case "ArrowUp":
                event.preventDefault()
                scrollToIndex(Math.max(currentImageIndex - 1, 0))
                return
            case "Home":
                event.preventDefault()
                scrollToIndex(0)
                return
            case "End":
                event.preventDefault()
                scrollToIndex(lastIndex)
                return
            case "Enter":
            case " ":
                event.preventDefault()
                handleImageClick(currentImageIndex)
        }
    }

    return (
        <>
            <div
                data-pdp-hero
                ref={containerRef}
                tabIndex={mediaCount > 0 ? 0 : undefined}
                role="region"
                aria-label={t(langCode, "product_page.image_gallery")}
                aria-roledescription="carousel"
                onKeyDown={handleGalleryKeyDown}
                className={clsx(
                    "no-scrollbar flex h-full w-full flex-1 snap-x snap-mandatory flex-col flex-nowrap overflow-x-auto scroll-smooth border-b border-b-col-border bg-col-bg-calm lg:h-full lg:snap-y lg:overflow-y-auto lg:border-b-0",
                    FOCUS_VISIBLE_CLASS
                )}
            >
                <div className="flex h-full w-max lg:w-full lg:flex-col">
                    {galleryMediaItems.map((item, index) => (
                        <div
                            key={index}
                            data-gallery-slide
                            className={clsx(
                                "relative aspect-[0.7] h-full w-screen snap-start snap-always border-b border-b-col-border lg:h-max lg:w-full lg:snap-none lg:snap-align-none",
                                index === mediaCount - 1 && "lg:border-b-0"
                            )}
                        >
                            {item._type === "image" && (
                                <BaseImage
                                    src={item.image || placeholder.src}
                                    alt={t(langCode, "product_page.image_alt", {
                                        name: productName,
                                        index: index + 1,
                                        total: galleryMediaItems.length
                                    })}
                                    fill
                                    className="object-cover object-[center_center]"
                                    sizes={imageSizesProduct}
                                    isAboveTheFold={index === 0}
                                />
                            )}
                            {item._type === "video" && (
                                <VideoElement
                                    vimeoId={item.vimeoId}
                                    videoFileUrl={item.videoFileUrl}
                                    autoplay={true}
                                    hideControls={true}
                                    mute={true}
                                    hasSound={false}
                                    loop={item.loop}
                                    poster={item.poster}
                                />
                            )}
                            <button
                                type="button"
                                tabIndex={index === currentImageIndex ? 0 : -1}
                                aria-label={t(
                                    langCode,
                                    "product_page.view_image_fullscreen",
                                    { current: index + 1, total: mediaCount }
                                )}
                                onClick={() => handleImageClick(index)}
                                className={clsx(
                                    "absolute inset-0 z-[1] cursor-pointer bg-transparent",
                                    FOCUS_VISIBLE_INSET_CLASS
                                )}
                            />
                        </div>
                    ))}
                    {mediaCount === 0 && (
                        <div className="relative h-full w-screen temp-md:w-full">
                            <Image
                                src={placeholder.src}
                                alt={productName}
                                fill
                                className="object-contain object-center"
                                unoptimized={true}
                            />
                        </div>
                    )}
                </div>

                {mediaCount > 1 && (
                    <div
                        role="group"
                        aria-label={t(langCode, "product_page.image_gallery")}
                        className="absolute bottom-8 left-12 z-10 flex w-fit gap-6 temp-md:bottom-16 temp-md:left-16 lg:flex-col"
                    >
                        {galleryMediaItems.map((_, index) => {
                            const isCurrent = index === currentImageIndex
                            return (
                                <button
                                    key={index}
                                    type="button"
                                    aria-label={t(langCode, "product_page.show_image", {
                                        current: index + 1,
                                        total: mediaCount
                                    })}
                                    aria-current={isCurrent ? "true" : undefined}
                                    onClick={() => handleIndicatorClick(index)}
                                    className={clsx(
                                        "relative h-6 w-6 rounded-full",
                                        "before:absolute before:inset-[-12px] before:content-['']",
                                        isCurrent
                                            ? "bg-col-bg-strong"
                                            : "bg-col-bg-subtle"
                                    )}
                                />
                            )
                        })}
                    </div>
                )}
            </div>
            {selectedImageIndex !== null && galleryMediaItems && (
                <FullscreenImagePopup
                    selectedImageIndex={selectedImageIndex}
                    onClose={() => setSelectedImageIndex(null)}
                    combinedMediaItems={galleryMediaItems}
                    langCode={langCode}
                    productName={productName}
                />
            )}
        </>
    )
}
